/**
 * One-shot, in-memory flag: "the navigation that is happening right now started
 * from a «ricerche recenti» row".
 *
 * ## Why this module exists at all (PLAN P1)
 *
 * A rerun from the history is exempt from the `offer-creation` quota guard on
 * `/result` (AC 10 / D10). The exemption cannot live in the URL — AC 10b requires
 * that copying the results URL, reloading it, or opening it from a bookmark still
 * hits the guard — so the results route needs an out-of-band signal.
 *
 * The obvious tool is the router's own navigation state,
 * `navigate(path, { state: … })`. **It is the wrong tool here.** Solid Router 0.16
 * stores that state in `history.state`, which the browser persists across a reload:
 * the guard would stay skipped on that entry forever, exactly the bypass AC 10b
 * forbids.
 *
 * A module-level flag has the lifetime we actually want:
 *
 * - it survives the `navigate` push (same JS realm), which is all the rerun needs;
 * - it dies with the realm — a reload, a copied URL, a bookmark, a new tab all
 *   start with it unset, so the guard applies (AC 10b);
 * - it is **never persisted**. Do not add it to `localStorage`, `localforage`, a
 *   cookie or the search store: persistence would recreate the `history.state` bug
 *   in a slower way.
 *
 * ## The accepted cost, stated out loud
 *
 * Browser **back → forward** onto a results page reached by a rerun goes through
 * the guard, because by then the flag is long consumed. That is deliberate: the
 * exemption belongs to "a navigation started from the row", not to the URL, and a
 * forward button press is not that. Do not "fix" it by re-arming the flag or by
 * moving it into history state.
 *
 * ## Why a plain binding and not `createSignal`
 *
 * Nothing reacts to this flag: its only reader consumes it once, imperatively, when
 * the `/result` route component mounts (T7). A signal would advertise a reactive
 * read that must never happen — a tracked read of a value that erases itself is a
 * trap. `undefined` reactivity is the point, so the state is a plain module binding.
 *
 * Writer: the row's rerun handler. Readers: the `/result` route component (which
 * feeds `skip`) and the search entry page (which drains a stale flag on mount, so a
 * navigation that never reaches `/result` — a redirect to login, say — cannot leave
 * it armed for the next «Nuova ricerca»). Both are wired in T7.
 */

let rerunIntent = false;

/** Arm the flag, immediately before the `navigate` push of a row rerun. */
export const markRecentSearchRerun = (): void => {
  rerunIntent = true;
};

/**
 * Read **and** clear the flag: `true` only for the first call after a
 * {@link markRecentSearchRerun}. Every consumer must treat the call as
 * destructive — calling it twice on the same navigation loses the exemption.
 */
export const consumeRecentSearchRerun = (): boolean => {
  const armed = rerunIntent;
  rerunIntent = false;
  return armed;
};

/**
 * Session flag (module-level, **not** one-shot): true while the currently
 * mounted `/result` page was reached by a rerun from «Le tue ricerche
 * recenti», false otherwise.
 *
 * Why a second flag and not {@link consumeRecentSearchRerun}: that one is
 * destructive and already drained at `/result` mount, by the quota-guard
 * component, before this flag's only reader — the in-page "← Indietro"
 * button (`navigateToSearch`) — ever runs. That button reconstructs
 * `/offers` or `/search` from the *current* filter state so a normal
 * from-offer/from-scratch search can carry edited filters back to the page
 * that produced them (a "modifica filtri" round trip, not a literal back).
 * A rerun never visited `/offers` or `/search`, so that reconstruction lands
 * the user on a page unrelated to where they came from. This flag lets the
 * button fall back to a real `navigate(-1)` in that one case.
 *
 * Read non-destructively (unlike {@link consumeRecentSearchRerun}) because
 * the button may be clicked, or not, an arbitrary number of times while
 * `/result` stays mounted. Cleared defensively at the two normal entry
 * points into `/result` (`offers/route.tsx` `handleJobOfferClick`,
 * `search/route.tsx` `onSubmit`) so a later, ordinary search cannot inherit
 * a stale `true` from an earlier rerun in the same tab.
 */
let resultReachedViaRerun = false;

export const markResultReachedViaRerun = (): void => {
  resultReachedViaRerun = true;
};

export const isResultReachedViaRerun = (): boolean => resultReachedViaRerun;

export const clearResultReachedViaRerun = (): void => {
  resultReachedViaRerun = false;
};
